import {
    ActionButton,
    ActionColumn,
    ActionRowSettings,
    BaseCollectionModel,
    BaseController,
    ColumnSettings,
    ExpandedStart,
    Pagination,
    SearchModel,
    TableSettings,
    YascOptionItem,
    YascTableComponent
} from '@yasc/yasc-material-components';
import {L10nTranslationService} from 'angular-l10n';
import {RestApiService} from '../../services/rest-api.service';
import {
    ContactPersonDetailRes,
    ProjectCreateReq,
    ProjectMinRes,
    ProjectSummaryAggregationRes,
    ProjectSummaryGroupingRes,
    ProjectUpdateReq,
    WorkflowDetailRes
} from '../../contracts/v0/crm';
import {Injectable} from '@angular/core';
import {RuntimeCacheService} from '../../services/runtime-cache.service';
import {MatDialog} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../../../partial-views/dialogs/confirmation-dialog/confirmation-dialog.component';
import {BehaviorSubject} from 'rxjs';
import {RequestCacheService} from '../../services/request-cache-service';
import {TrackerType} from '../../enums';

@Injectable({
    providedIn: 'root'
})
export class ProjectController extends BaseController<ProjectMinRes, ProjectUpdateReq, ProjectCreateReq, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes> {
    override yascDataType = ProjectMinRes;
    projects: ProjectMinRes[] = [];
    projectSearchLoaded$ = new BehaviorSubject<boolean>(false);

    workflows: WorkflowDetailRes[] = [];
    workflowOptions: YascOptionItem<number>[] = []
    customers: YascOptionItem<number>[] = []


    constructor(
        private restApiService: RestApiService,
        private translate: L10nTranslationService,
        private requestCacheService: RequestCacheService,
        runTime: RuntimeCacheService,
        matDialog: MatDialog,
    ) {
        super();
        // @ts-ignore
        this.dialog = matDialog
        this.runTimeCache = runTime
        this.contentViewActionBtn = new ActionRowSettings({prefabButtons: [ActionButton.Create, ActionButton.ImportExcel, ActionButton.Delete]})
        this.yascDataSource.Settings = this.getTableSettings();
        this.restApiService.v0.company.search().subscribe(value => {
            this.customers = value.data.map(cus => new YascOptionItem(cus.name, cus.id))
        })

        this.restApiService.v0.crm.workflow.search().subscribe(value => {
            this.workflows = value.data
            this.workflowOptions = value.data.filter(x => x.type === TrackerType.Project).map(x => new YascOptionItem(x.name, x.id))
        })
    }


    override toUpdateRequest(val: ProjectMinRes): ProjectUpdateReq {
        let test = new ProjectUpdateReq({
            name: val.name,
            offerNr: val.offerNr,
            orderNr: val.orderNr,
            comment: val.comment,
            isActive: val.isActive,
            projectVolumeBrutto: val.projectVolumeBrutto,
            projectVolumeBruttoOpen: val.projectVolumeBruttoOpen,
            currentWorkflowStatus: val.currentWorkflowStatus,
            workflowId: val.workflowId,
            type: val.type,
            customer: val.customer

        })
        return test
    }

    override toCreateRequest(val: ProjectMinRes): ProjectCreateReq {
        return ProjectCreateReq.FromMinDetails(val)
    }

    override onEdit(val?: ProjectUpdateReq | undefined): void {
        if (this.currentCreateItem === undefined) return
        this.update(val)
    }

    override onCreate(val?: ProjectCreateReq | undefined): void {
        this.add(val)
    }

    override onCopy(): void {
        this.add()
    }

    override onCancel(): void {
        throw new Error("Method not implemented.");
    }

    override onSave(): void {
        this.update()
    }


    override onDelete(val?: any): void {
        let toDelete = []
        if (val !== undefined) toDelete.push(val)
        else toDelete = this.table?.selectedRows.selected ?? []

        if (toDelete.length === 0) return

        this.dialog!.open(ConfirmationDialogComponent, {
            data: {
                text: this.translate.translate('crm_contacts_multiDelete_text', {number: toDelete.length}, this.locale.language),
            }
        }).afterClosed().subscribe(x => {
            if (x === null) return
            let ids: number[] = toDelete.map((y) => (y as ContactPersonDetailRes).id)
            if (x) {
                this.delete(ids)
            }
        })
    }

    override onBulkPost(): void {
        throw new Error("Method not implemented.");
    }

    override onBulkUpdate(val: ProjectUpdateReq[]): void {
        throw new Error("Method not implemented.");
    }

    public table!: YascTableComponent<ProjectMinRes>


    override getColumnSettings(optional?: ColumnSettings<ProjectMinRes>[]): ColumnSettings<ProjectMinRes>[] {
        return super.getColumnSettings(optional);
    }

    override getTableSettings(): TableSettings<ProjectMinRes> {
        let settings = new TableSettings<ProjectMinRes>({
            yascDatatype: ProjectMinRes,
            yascLabel: this.translate.translate('general_project'),
            yascShowFilters: true,
            yascShowColumnsMenu: true,
            yascStickyHeader: true,
            yascMultiSelect: true,
            yascInlineEditing: false,
            yascRowClick: false,
            yascShowIndexCol: true,
            yascResizeColumns: true,
            yascGroupingHeaders: true,
            yascShowDetailSlider: true,
            yascStartExpanded: ExpandedStart.AllExpanded,
            yascActionColumns: [ActionColumn.Edit, ActionColumn.Delete],
        });

        return settings;
    }

    override loadData(searchModel?: SearchModel): Promise<BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes>> {
        searchModel ??= new SearchModel({
            sorting: [],
            filters: [],
            pagination: new Pagination({
                index: 0,
                size: 150
            })
        });

        const backendSearch = this.yascDataSource.buildBackendSearchModel(searchModel);
        const cacheKey = this.buildProjectSearchCacheKey(backendSearch);

        return this.requestCacheService.getCached<BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes>>({
            key: cacheKey,
            // @ts-ignore
            loader: () => this.restApiService.v0.crm.project.search(backendSearch),
            onCached: (val) => {
                this.applyProjects(val, searchModel);
                this.projectSearchLoaded$.next(true);
            },
            onSuccess: (val) => {
                this.applyProjects(val, searchModel);
                this.projectSearchLoaded$.next(true);
            },
            onError: () => {
                this.projectSearchLoaded$.next(false);
            }
        });
    }

    private applyProjects(
        val: BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes>,
        searchModel?: SearchModel
    ): BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes> {
        this.yascDataSource.applyCollectionResponse(val, searchModel);
        console.log(this.yascDataSource);
        this.projects = val.data;
        return val;
    }

    private buildProjectSearchCacheKey(searchModel: SearchModel): string {

        return `crm.projects.min:${JSON.stringify({
            filters: searchModel.filters ?? [],
            sorting: searchModel.sorting ?? [],
            pagination: {
                index: searchModel.pagination?.index ?? 0,
                size: searchModel.pagination?.size ?? 50
            }
        })}`;
    }

    override add(req?: ProjectCreateReq) {
        req = req ?? this.currentCreateItem as ProjectCreateReq;
        req.projectVolumeBrutto = parseFloat(req.projectVolumeBrutto!.toString().replace(',', '.'))
        this.restApiService.v0.crm.project.create((req as ProjectCreateReq)!).subscribe({
            next: (val: ProjectMinRes) => {
                this.currentItem = val;
                this.yascDataSource.TableData.push(val);
                this.table.tableCur?.renderRows()
            }
        })
    }

    override update(req?: ProjectUpdateReq) {
        req = req ?? this.currentCreateItem as ProjectUpdateReq;
        this.restApiService.v0.crm.project.patchProject(req!.id, req!).subscribe({
            next: (val: ProjectMinRes) => {
                this.currentItem = val;
                this.yascDataSource.data[this.yascDataSource.data.findIndex(x => val.id! === x.id)] = val;
                this.table.tableCur?.renderRows()
            }
        })
    }

    delete(id: number[]) {
        this.restApiService.v0.crm.project.bulkDelete(id).subscribe({
            next: (_) => {
                this.yascDataSource.RemoveItemsFormTableData(id, (val: ProjectMinRes) => val.id)
                this.table.tableCur?.renderRows()
            }
        })
    }

    override assignOptionsToFields = (path: string): {
        compareFn?: (a: any, b: any) => boolean,
        options: string[] | YascOptionItem<any>[],
    } | undefined => {
        let result = undefined;
        // TODO: fix and move logic to component not in controller

        switch (path) {
            case 'customer':
                result = {
                    compareFn: (a: YascOptionItem<number>, b: YascOptionItem<number>) => a.value === b.value,
                    options: this.customers
                }
                break;
            case 'currentWorkflowStatus':
                if (this.currentCreateItem === undefined) {
                    result = undefined;
                } else {
                    console.log(this.currentCreateItem)
                    console.log(this.workflows[this.workflows.findIndex(x => x.id === this.currentCreateItem?.workflowId)])
                    result = {
                        compareFn: (a: YascOptionItem<number>, b: YascOptionItem<number>) => a.value === b.value,
                        options: this.workflows[this.workflows.findIndex(x => x.id === this.currentCreateItem?.workflowId)].states.map(x => new YascOptionItem(x.name, x.id))
                    }
                }
                break
            case 'workflow':
                result = {
                    compareFn: (a: YascOptionItem<number>, b: YascOptionItem<number>) => a.value === b.value,
                    options: this.workflowOptions
                }
                break
            default:
                super.assignOptionsToFields(path)
                break;
        }
        return result;
    }


}